
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Lock, Crown, X } from 'lucide-react';
import { groupedItems, categoryLabels, NavItem } from './SidebarData';
import { cn } from '@/lib/utils';
import { useEnhancedSubscription } from '@/hooks/useEnhancedSubscription';
import { useSidebar } from '@/components/ui/sidebar';

interface CategoryConfig {
  name: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

export function MobileSidebarContent() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const location = useLocation();
  const { subscription } = useEnhancedSubscription();
  const { setOpenMobile } = useSidebar();
  const currentTier = subscription?.tier || 'freemium';

  const handleCategoryTap = (categoryName: string) => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    setExpandedCategory(expandedCategory === categoryName ? null : categoryName);
  };

  const handleItemClick = () => {
    // Close sidebar after navigation
    setOpenMobile(false);
    setExpandedCategory(null);
  };

  const isItemActive = (path: string) => location.pathname === path;
  const isItemLocked = (item: NavItem) => {
    const isProFeature = item.isProFeature || item.isPro;
    return isProFeature && currentTier === 'freemium';
  };

  // Get first item's icon for category
  const getCategoryIcon = (items: NavItem[]) => {
    if (items.length > 0) {
      return items[0].icon;
    }
    return null;
  };

  // Build category configs
  const categories: CategoryConfig[] = Object.entries(groupedItems)
    .filter(([_, items]) => items && items.length > 0)
    .map(([category, items]) => ({
      name: category,
      label: categoryLabels[category as keyof typeof categoryLabels] || category,
      icon: getCategoryIcon(items) || (() => null),
      items: items
    }));

  return (
    <div className="flex h-full">
      {/* Icon rail - always visible */}
      <div className="w-16 flex flex-col items-center py-3 border-r border-border bg-sidebar shrink-0">
        {categories.map((cat) => {
          const IconComponent = cat.icon;
          const isExpanded = expandedCategory === cat.name;
          const hasActiveItem = cat.items.some(item => isItemActive(item.path));
          
          return (
            <button
              key={cat.name}
              onClick={() => handleCategoryTap(cat.name)}
              className={cn(
                "w-12 h-12 flex items-center justify-center rounded-lg mb-1 transition-all",
                "active:scale-95 touch-manipulation",
                isExpanded && "bg-primary/10 text-primary",
                hasActiveItem && !isExpanded && "bg-accent text-accent-foreground",
                !isExpanded && !hasActiveItem && "text-muted-foreground hover:bg-accent/50"
              )}
              aria-label={cat.label}
              aria-expanded={isExpanded}
            >
              <IconComponent className="h-5 w-5" />
            </button>
          );
        })}
      </div>

      {/* Expanded labels panel - slides in on tap */}
      <AnimatePresence mode="wait">
        {expandedCategory && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 220, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="border-r border-border bg-sidebar overflow-hidden"
          >
            <div className="w-[220px] h-full flex flex-col">
              {/* Category header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="font-semibold text-sm text-foreground">
                  {categories.find(c => c.name === expandedCategory)?.label}
                </h3>
                <button
                  onClick={() => setExpandedCategory(null)}
                  className="p-1 rounded-md hover:bg-accent"
                  aria-label="Close category"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>

              {/* Items list */}
              <div className="flex-1 overflow-y-auto py-2 px-2">
                {categories
                  .find(c => c.name === expandedCategory)
                  ?.items.map((item) => {
                    const isActive = isItemActive(item.path);
                    const isLocked = isItemLocked(item);
                    const ItemIcon = item.icon;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleItemClick}
                        className={cn(
                          "flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors",
                          "active:scale-[0.98] touch-manipulation min-h-[48px]",
                          isActive && "bg-primary/10 text-primary font-medium",
                          !isActive && "text-foreground hover:bg-accent"
                        )}
                      >
                        <ItemIcon className="h-4 w-4 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.label}</p>
                          {item.fullName !== item.label && (
                            <p className="text-xs text-muted-foreground truncate">
                              {item.fullName}
                            </p>
                          )}
                        </div>
                        {isLocked ? (
                          <Crown className="h-4 w-4 text-amber-500 shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                      </Link>
                    );
                  })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
